import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useCreateCompany } from "@/hooks/company/use-company";
import { useSwitchCompany } from "@/hooks/company-membership/use-company-membership";
import { useCompanyStore } from "@/store/use-company-store";
import { companyOnboardingService } from "@/services/company-onboarding/company-onboarding.service";
import { loadProgress, saveProgress, clearProgress, type CompanyCreationProgress } from "@/utils/create-company-progress";

export function useCreateCompanyFlow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { currentCompanyId } = useCompanyStore();
  const createCompanyMutation = useCreateCompany();
  const switchCompanyMutation = useSwitchCompany();

  const urlCompanyId = searchParams.get('companyId');
  const storedProgress = loadProgress();
  const initialCompanyId = storedProgress?.companyId || urlCompanyId || undefined;

  // Always start at step 1 if no stored progress exists
  // Stored progress is only used if user was actively in the middle of creating a company
  const [currentStep, setCurrentStep] = useState(storedProgress?.step || 1);
  const [companyId, setCompanyId] = useState<string | undefined>(initialCompanyId);
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(undefined);
  const isCompletingRef = useRef(false);

  useEffect(() => {
    const saved = loadProgress();
    if (saved) {
      // Restore stored progress if user was actively creating
      if (saved.step && saved.step !== currentStep) {
        setCurrentStep(saved.step);
      }
      if (saved.companyId && saved.companyId !== companyId) {
        setCompanyId(saved.companyId);
      }
    }

    const urlPlanId = searchParams.get('planId');
    if (urlPlanId && currentStep === 3) {
      setSelectedPlanId(urlPlanId);
    }
  }, []);

  useEffect(() => {
    // If companyId is in URL but no stored progress, set companyId but stay on step 1
    // This allows editing/verification of existing company details
    if (urlCompanyId && !storedProgress) {
      setCompanyId(urlCompanyId);
      // Always start at step 1, don't skip steps
      setCurrentStep(1);
    }
  }, [urlCompanyId, storedProgress]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    saveProgress({
      step: currentStep,
      companyId,
    });
  }, [currentStep, companyId]);

  const handleStep1Complete = async () => {
    try {
      // First verify company was created successfully
      await queryClient.invalidateQueries({ queryKey: ['company'] });
      await queryClient.invalidateQueries({ queryKey: ['userMemberships'] });

      const updatedCompany = await queryClient.fetchQuery({
        queryKey: ['company', currentCompanyId],
        queryFn: async () => {
          const { companyService } = await import('@/services/company/company.service');
          return companyService.getCompany();
        },
      });

      // Verify company exists before proceeding
      if (!updatedCompany?.id) {
        toast.error(t('onboarding.companyCreationFailed') || 'Failed to create company. Please try again.');
        // Stay on step 1 - don't advance
        return;
      }

      // Company exists, now try to switch to it
      try {
        await switchCompanyMutation.mutateAsync({ companyId: updatedCompany.id, suppressToast: true });
      } catch (switchError) {
        // If switch fails, log but still allow progression since company exists
        console.warn('Failed to switch company, but company was created:', switchError);
      }

      // Only advance step if company was verified to exist
      setCompanyId(updatedCompany.id);
      saveProgress({
        step: 2,
        companyId: updatedCompany.id,
      });
      setCurrentStep(2);
    } catch (error) {
      // If any step fails, show error and stay on current step
      const errorMessage = error instanceof Error ? error.message : t('onboarding.companyCreationFailed') || 'Failed to verify company creation';
      toast.error(errorMessage);
      // Don't advance step - stay on step 1
    }
  };

  const handleStep2Complete = (planId?: string) => {
    if (!planId) {
      toast.error(t('onboarding.toasts.pleaseSelectPlan') || 'Please select a plan');
      return;
    }

    // Verify company exists before proceeding to checkout
    if (!companyId) {
      toast.error(t('onboarding.companyRequired') || 'Company is required. Please complete company details first.');
      setCurrentStep(1);
      return;
    }

    setSelectedPlanId(planId);

    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.set('planId', planId);
    navigate(`${window.location.pathname}?${newSearchParams.toString()}`, { replace: true });

    saveProgress({
      step: 3,
      companyId,
    });
    setCurrentStep(3);
  };

  const handleStep3Complete = async () => {
    if (!companyId) return;

    // Prevent multiple simultaneous completion checks
    if (isCompletingRef.current) {
      return;
    }

    isCompletingRef.current = true;

    try {
      await queryClient.invalidateQueries({ queryKey: ["companyOnboardingStatus", companyId] });
      let status = await companyOnboardingService.getStatus(companyId);

      // Poll for completion confirmation
      let attempts = 0;
      while (!status.isComplete && attempts < 5) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        await queryClient.invalidateQueries({ queryKey: ["companyOnboardingStatus", companyId] });
        status = await companyOnboardingService.getStatus(companyId);
        attempts++;
      }

      if (status.isComplete) {
        clearProgress();
        localStorage.removeItem('pendingInvitationEmails');

        queryClient.removeQueries({ queryKey: ['companyOnboardingStatus', companyId] });
        queryClient.invalidateQueries({ queryKey: ['userMemberships'] });

        const { useCompanyStore } = await import('@/store/use-company-store');
        const { currentCompanyId, setCurrentCompany } = useCompanyStore.getState();
        if (currentCompanyId !== companyId) {
          setCurrentCompany(companyId);
        }

        toast.success(t('onboarding.complete.title') || 'Setup Complete!');
        navigate('/', { replace: true });
      } else {
        // If not complete after polling, stay on step 3
        setCurrentStep(3);
      }
    } catch {
      // Silent fail - reset flag on error
      isCompletingRef.current = false;
    } finally {
      // Reset flag after a delay to allow for retries
      setTimeout(() => {
        isCompletingRef.current = false;
      }, 2000);
    }
  };

  const handleStepComplete = (planId?: string) => {
    switch (currentStep) {
      case 1:
        handleStep1Complete();
        break;
      case 2:
        handleStep2Complete(planId);
        break;
      case 3:
        handleStep3Complete();
        break;
    }
  };

  return {
    currentStep,
    companyId,
    selectedPlanId,
    handleStepComplete,
  };
}

